'use client'

import { Button, Cell, Spacer } from '@vinted/web-ui'
import { assign, camelCase, isEmpty } from 'lodash'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { isFullNameValid } from '@marketplace-web/registration/identity-feature'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { isValueInObject } from '@marketplace-web/shared/utils'
import {
  CountryModel,
  transformCountryDtos,
  getCountries,
} from '@marketplace-web/country/country-data'

import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'
import {
  AddressField,
  AddressFieldConfiguration,
  AddressFieldTranslationModel,
  AddressModel,
  PostalCodeModel,
  transformPostalCodeDto,
  getPostalCodeConfiguration,
} from '@marketplace-web/address/address-data'

import AddressFormFields from './AddressFormFields'
import { Field } from './types'
import { snakeCase } from '../../utils/string'

type Props = {
  isSubmitPending: boolean
  isDeleteVisible?: boolean
  styling?: React.ComponentProps<typeof Cell>['styling']
  errors: Array<ErrorItem>
  defaultValues?: Partial<AddressModel> | null
  loader?: ReactElement
  translations?: AddressFieldTranslationModel
  fieldConfiguration?: Array<AddressFieldConfiguration>
  infoBannerProps?: React.ComponentProps<typeof InfoBanner>
  onSubmit: (address: AddressModel, isFormValid: boolean) => void
  onCancel: () => void
  onDelete?: () => void
}

const defaultFieldConfiguration = [
  { field: AddressField.Name },
  { field: AddressField.Country },
  { field: AddressField.Line1, required: true },
  { field: AddressField.Line2 },
  { field: AddressField.PostalCode, required: true },
  { field: AddressField.City, required: true },
]

const addressFieldToFormFieldMap = {
  [AddressField.Country]: Field.CountryId,
  [AddressField.PostalCode]: Field.PostalCode,
  [AddressField.Name]: Field.Name,
  [AddressField.Line1]: Field.Line1,
  [AddressField.Line2]: Field.Line2,
  [AddressField.City]: Field.City,
}

const AddressForm = ({
  isSubmitPending,
  isDeleteVisible,
  styling,
  errors,
  defaultValues,
  loader,
  translations,
  fieldConfiguration = defaultFieldConfiguration,
  infoBannerProps,
  onSubmit,
  onCancel,
  onDelete,
}: Props) => {
  const { country_id: userCountryId, business_account: isBusinessAccount } = useSession().user || {}

  const { messages } = useIntl()
  const { track } = useTracking()

  const translateAddressForm = useTranslate('address_form')
  const translateErrors = useTranslate('errors')

  const formMethods = useForm<AddressModel>({
    defaultValues: {
      ...defaultValues,
      countryId: defaultValues?.countryId || userCountryId,
    },
  })

  const {
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors: formErrors },
  } = formMethods

  const postalCode = watch(Field.PostalCode)
  const countryId = watch(Field.CountryId)

  const [loadState, setLoadState] = useState<UiState>(UiState.Idle)
  const [countries, setCountries] = useState<Array<CountryModel>>([])
  const [postalCodeConfiguration, setPostalCodeConfiguration] = useState<PostalCodeModel | null>(
    null,
  )

  const postalCodeConstraints: {
    format: string
    minLength: number
    maxLength: number
  } | null = useMemo(() => {
    const country = countries.find(({ id }) => Number(countryId) === id)

    // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion
    if (!country) return countries[0]!?.postalCodeConstraints

    return country.postalCodeConstraints
  }, [countries, countryId])

  const fetchPostalCode = useCallback(
    async (code: string, country: number) => {
      const response = await getPostalCodeConfiguration({
        code,
        countryId: country,
      })

      if ('errors' in response) {
        setError(Field.PostalCode, {
          type: 'manual',
          message: translateAddressForm('validation.wrong_postal_code'),
        })
        setPostalCodeConfiguration(null)

        return
      }

      const transformedPostalCodeConfiguration = transformPostalCodeDto(response.postal_code)

      clearErrors(Field.PostalCode)

      setPostalCodeConfiguration(transformedPostalCodeConfiguration)
    },
    [setError, translateAddressForm, clearErrors],
  )

  const fetchCountries = useCallback(async () => {
    setLoadState(UiState.Pending)

    const response = await getCountries()

    if (!response) return
    if ('errors' in response) return

    const transformedCountries = transformCountryDtos(response.countries)

    setLoadState(UiState.Success)
    setCountries(transformedCountries)
  }, [])

  useEffect(() => {
    if (countries.length === 0 && errors.length === 0 && isEmpty(formErrors)) fetchCountries()
  }, [fetchCountries, countries.length, errors.length, formErrors])

  useEffect(() => {
    if (!postalCode || !countryId || !postalCodeConstraints) return

    if (String(postalCode).length < postalCodeConstraints?.minLength) {
      setPostalCodeConfiguration(null)

      return
    }

    fetchPostalCode(postalCode, countryId)
  }, [countryId, postalCode, postalCodeConstraints, fetchPostalCode])

  useEffect(() => {
    errors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, Field)) return

      setError(fieldName, { type: 'manual', message: value })
    })
  }, [errors, setError])

  const getFieldConfiguration = (addressField: AddressField) =>
    fieldConfiguration?.find(({ field }) => field === addressField)

  const getRequiredFieldErrorMessage = (field: AddressField) => {
    const requiredMessagePath = `${snakeCase(field)}.errors.required`

    if (messages[`address_form.${requiredMessagePath}`]) {
      return translateAddressForm(requiredMessagePath)
    }

    const fieldTitle = translations?.[field]?.title || translateAddressForm(`${field}.title`)

    return translateErrors('blank', { field: fieldTitle })
  }

  const validateField = (field: AddressField, value: string | number) => {
    const currentFieldConfiguration = getFieldConfiguration(field)

    if (currentFieldConfiguration?.required && isEmpty(value)) {
      setError(addressFieldToFormFieldMap[field], {
        type: 'manual',
        message: getRequiredFieldErrorMessage(field),
      })

      return false
    }

    if (!isBusinessAccount && field === AddressField.Name && !isFullNameValid(value.toString())) {
      if (isEmpty(value)) {
        setError(addressFieldToFormFieldMap[field], {
          type: 'manual',
          message: getRequiredFieldErrorMessage(field),
        })
      } else {
        setError(addressFieldToFormFieldMap[field], {
          type: 'manual',
          message: translateAddressForm('validation.invalid_full_name'),
        })
      }

      return false
    }

    if (field === AddressField.PostalCode && formErrors[Field.PostalCode]) {
      return false
    }

    clearErrors(addressFieldToFormFieldMap[field])

    return true
  }

  const handleBlur = (field: AddressField, value: string) => {
    const valid = validateField(field, value)
    const trackingEvent = getFieldConfiguration(field)?.trackingEvent

    if (!trackingEvent?.extra) return

    if ('valid' in trackingEvent.extra) {
      assign(trackingEvent.extra, { valid })
    }

    track(trackingEvent)
  }

  const validateForm = () => {
    if (!isEmpty(formErrors)) return false
    if (!fieldConfiguration?.length) return true

    return fieldConfiguration
      .map(({ field }) => validateField(field, getValues(addressFieldToFormFieldMap[field]) ?? ''))
      .every(Boolean)
  }

  const handleFormSubmit = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (isSubmitPending) {
      event.preventDefault()

      return
    }

    const data = getValues()

    onSubmit(
      {
        countryId,
        name: data.name,
        line1: data.line1,
        line2: data.line2,
        postalCode: postalCodeConfiguration?.code || null,
        state: postalCodeConfiguration?.state || null,
        city: data.city,
      },
      validateForm(),
    )
  }

  const renderBottomButton = () => {
    if (isDeleteVisible && onDelete) {
      return (
        <Button
          theme="warning"
          disabled={isSubmitPending}
          text={translations?.buttons?.delete || translateAddressForm('buttons.delete')}
          onClick={onDelete}
        />
      )
    }

    return (
      <Button
        styling={Button.Styling.Flat}
        disabled={isSubmitPending}
        text={translations?.buttons?.cancel || translateAddressForm('buttons.cancel')}
        onClick={onCancel}
      />
    )
  }

  const renderLoader = () => {
    if (loader) return loader

    return <ContentLoader styling={ContentLoader.Styling.Wide} />
  }

  const renderFormFields = () => (
    <AddressFormFields
      fieldConfiguration={fieldConfiguration}
      postalCode={postalCode}
      postalCodeConfiguration={postalCodeConfiguration}
      postalCodeConstraints={postalCodeConstraints}
      countries={countries}
      countryId={countryId}
      city={getValues(AddressField.City)}
      translations={translations}
      infoBannerProps={infoBannerProps}
      onBlur={handleBlur}
    />
  )

  if (loadState !== UiState.Success) return renderLoader()
  if (!postalCodeConstraints || !fieldConfiguration) return null

  return (
    <FormProvider {...formMethods}>
      {renderFormFields()}
      {!!translations?.note && <Cell styling={styling} body={translations.note} />}
      <Cell styling={styling}>
        <Button
          testId="address-form-submit-button"
          styling={Button.Styling.Filled}
          disabled={isSubmitPending}
          isLoading={isSubmitPending}
          text={translations?.buttons?.continue || translateAddressForm('buttons.continue')}
          theme="primary"
          onClick={handleFormSubmit}
        />
        <Spacer size={Spacer.Size.Medium} />
        {renderBottomButton()}
      </Cell>
    </FormProvider>
  )
}

export default AddressForm
